import './ForgotPassword.scss'

import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import {
  Button,
  Col,
  Container,
  FloatingLabel,
  Form,
  Row
} from 'react-bootstrap'

import Error from 'components/common/error/Error'
import {
  confirmForgotPasswordCode,
  sendForgotPasswordCode
} from 'components/common/api/Users'
import { isEmail } from 'components/common/helpers/Strings'
import PasswordGroup from '../PasswordGroup'

import RembleLogomark from '../../../assets/images/Remble_Logomark-Green.png'
import { navigateTo } from 'components/common/navigation'
import { ROUTE_LOGIN } from 'components/common/helpers/Paths'

export default function ForgotPassword({ setHeaderTitle, setReturnPath }) {
  const history = useHistory()

  const [loading, setLoading] = useState(false)
  const [codeSent, setCodeSent] = useState(false)
  const [isValidEmail, setIsValidEmail] = useState(true)
  const [isValidPassword, setIsValidPassword] = useState(false)
  const [verificationCode, setVerificationCode] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [forgotPasswordError, setForgotPasswordError] = useState()
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState()
  const [emailSentSuccess, setEmailSentSuccess] = useState()

  const [isValidResetForm, setIsValidResetForm] = useState(false)

  /**
   * Sends the user a code to reset their password
   */
  const sendForgotPasswordEmail = async event => {
    // Return early if we don't have a valid email address
    if (!isValidEmail) return

    // Reset error
    setForgotPasswordError(null)
    setLoading(true)

    // Send the email
    const result = await sendForgotPasswordCode(email)
    const success = result.success
    setLoading(false)

    // Did we have an error?
    if (!success) {
      const message =
        result.detail ||
        result.message ||
        result.msg ||
        'Unable to send password reset code'
      setForgotPasswordError(String(message))
      return
    }

    // Email sent, let's show the verification code form
    setEmailSentSuccess(true)
  }

  const requestNewCode = async event => {
    // Reset the form
    setCodeSent(true)
    sendForgotPasswordEmail(event)
  }

  /**
   * Sets and runs validation on `forgotPasswordEmail` field
   * @param {String} value The field's value
   */
  const setAndValidateForgotPasswordEmail = value => {
    const isValid = isEmail(value)

    setEmail(value)
    setIsValidEmail(isValid)
  }

  /**
   * Handles the user's submission of the forgot password form
   * @param {Event} event The event that triggered the submission
   */
  const handleSubmit = async event => {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    // Return early if we don't have a valid reset form
    if (!isValidResetForm) return
    setLoading(true)
    setForgotPasswordError(null)
    setCodeSent(false)

    const result = await confirmForgotPasswordCode(
      email,
      verificationCode,
      password
    )

    const success = result.success

    // Did we have an error?
    if (!success) {
      const message =
        result.detail ||
        result.message ||
        'Unable to reset password. Try again in a few minutes or contact support@remble.com.'
      setForgotPasswordError(String(message))
      setLoading(false)
      return
    }

    // Password reset, let's show the success message
    setForgotPasswordSuccess(true)
    setLoading(false)
  }

  /**
   * Simple effect
   */
  useEffect(() => {
    setHeaderTitle('Forgot Password')
    setReturnPath('/')
  }, [setHeaderTitle, setReturnPath])

  return (
    <Container fluid noValidate className='forgot-password-container'>
      <Row>
        <Col>
          <img
            className='logomark'
            src={RembleLogomark}
            alt='Remble'
            width='25'
            fetchpriority='high'
          />

          <Form className='verify-form' onSubmit={handleSubmit}>
            <Error error={forgotPasswordError} className={'mb-1'} />
            {!emailSentSuccess ? (
              <>
                <p className='subtitle mt-3'>
                  We will email you an{' '}
                  <span className='muted'>account verification code</span> to
                  reset your password
                </p>
                <Form.Group controlId='formForgotPasswordEmail'>
                  <FloatingLabel
                    controlId='floatingForgotPasswordEmail'
                    label='Your email address'
                  >
                    <Form.Control
                      name='forgotPasswordEmail'
                      disabled={loading}
                      autoFocus
                      size='lg'
                      placeholder='Your email address'
                      type='email'
                      onChange={e =>
                        setAndValidateForgotPasswordEmail(e.currentTarget.value)
                      }
                      onKeyDown={e =>
                        e.key === 'Enter' && sendForgotPasswordEmail()
                      }
                      isInvalid={!isValidEmail}
                      isValid={email && isValidEmail}
                    />
                    <Form.Control.Feedback type='invalid'>
                      Please provide a valid email address
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Form.Group>
                <Row className='mt-4'>
                  <Col>
                    <Button
                      variant='primary-outline'
                      onClick={() => navigateTo('/', history)}
                    >
                      Cancel
                    </Button>
                  </Col>
                  <Col className='text-right'>
                    <Button
                      disabled={!email || !isValidEmail || loading}
                      variant='primary'
                      onClick={() => sendForgotPasswordEmail()}
                    >
                      Send Email
                    </Button>
                  </Col>
                </Row>
              </>
            ) : (
              <>
                {/* Did we successfully reset the password? */}
                {forgotPasswordSuccess ? (
                  <>
                    <h3 className='mt-3 title'>Password Reset Successfully</h3>
                    <p className='subtitle mt-3'>
                      Your password has been reset. You may now use your new
                      password to login
                    </p>
                    <div className='text-right mt-4'>
                      <Button
                        variant='primary'
                        className='px-4'
                        onClick={() => navigateTo(ROUTE_LOGIN, history)}
                      >
                        Return to Login
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <h3 className='mt-3 title'>Check your email</h3>
                    <p className='subtitle'>
                      We've emailed an{' '}
                      <span className='muted'>account verification code</span>{' '}
                      to <span className='green'>{email}</span>. You will need
                      this code to reset your password.
                    </p>
                    <Form.Group
                      className='form-group visible my-3'
                      controlId='formCode'
                    >
                      <FloatingLabel
                        controlId='floatingCode'
                        label='Account verification code'
                      >
                        <Form.Control
                          name='code'
                          disabled={loading}
                          size='lg'
                          placeholder='Account verification code'
                          required
                          autoFocus={true}
                          autoCapitalize='off'
                          type='text'
                          value={verificationCode}
                          onChange={e => {
                            const value = e.currentTarget.value
                            setVerificationCode(value)
                            setIsValidResetForm(!!value && isValidPassword)
                          }}
                        />
                        <Form.Control.Feedback type='invalid'>
                          Verification code is required
                        </Form.Control.Feedback>
                      </FloatingLabel>
                    </Form.Group>

                    {!verificationCode && (
                      <div className='muted mt-5 mb-3 text-center'>
                        <p>Didn't get the email?</p>
                        <Button
                          variant='secondary-outline'
                          disabled={loading || codeSent}
                          className='ml-2'
                          onClick={requestNewCode}
                        >
                          <span>
                            {codeSent ? 'Sent! 🙂' : 'Request a new code'}
                          </span>
                        </Button>
                        {codeSent && (
                          <p className='mt-5'>
                            <small>
                              Still not getting the email? <br />
                              Email Remble support:{' '}
                              <a href='mailto:support@remble.com'>
                                support@remble.com
                              </a>
                            </small>
                          </p>
                        )}
                      </div>
                    )}

                    <div
                      className={`password-wrapper ${
                        verificationCode ? 'visible' : ''
                      }`}
                    >
                      <h4 className='mt-5'>New Password</h4>

                      {/* Password group field */}
                      <PasswordGroup
                        isNewPassword={true}
                        showForgotPassword={false}
                        loading={loading}
                        onValidStateChange={isValid => {
                          // Only update the state if we've already set it to true
                          if (isValidResetForm) setIsValidResetForm(isValid)
                        }}
                        onPasswordsValid={isEnterKey => {
                          // If our password confirmation looks good, let's update the form state
                          setIsValidResetForm(!!verificationCode) // Do we have a valid code?
                          setIsValidPassword(true)
                          if (isEnterKey) handleSubmit()
                        }}
                        onSetPassword={password => {
                          setPassword(password)
                        }}
                        handleSubmit={handleSubmit}
                      />
                    </div>

                    <Row className='mt-4'>
                      <Col xs={4}>
                        <Button
                          variant='primary-outline'
                          onClick={() => navigateTo('/', history)}
                        >
                          Cancel
                        </Button>
                      </Col>
                      <Col className='text-right'>
                        <Button
                          variant='primary'
                          className='px-4'
                          disabled={loading || !isValidResetForm}
                          onClick={handleSubmit}
                        >
                          Reset Password
                        </Button>
                      </Col>
                    </Row>
                  </>
                )}
              </>
            )}
          </Form>
        </Col>
      </Row>
    </Container>
  )
}
